import React, { forwardRef, useImperativeHandle, useMemo, useRef } from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

const RichTextEditor = forwardRef(({ value, onChange, placeholder = 'Enter content here...', onFocus }, ref) => {
  const quillRef = useRef(null);
  const lastRangeRef = useRef(null);

  const modules = useMemo(() => ({
    toolbar: [
      ['bold', 'italic', 'underline'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ align: [] }]
    ]
  }), []);

  useImperativeHandle(ref, () => ({
    insertTextAtCursor: (text) => {
      const editor = quillRef.current?.getEditor?.();
      if (!editor) return false;

      const range = editor.getSelection() || lastRangeRef.current;
      const safeText = String(text ?? '');

      if (range) {
        editor.insertText(range.index, safeText);
        editor.setSelection(range.index + safeText.length, 0);
      } else {
        const end = editor.getLength() - 1;
        editor.insertText(Math.max(0, end), safeText);
        editor.setSelection(Math.max(0, end) + safeText.length, 0);
      }

      onChange(editor.root.innerHTML);
      editor.focus();
      return true;
    }
  }), [onChange]);

  const wrapperStyle = {
    border: '1px solid var(--border)',
    borderRadius: '8px',
    overflow: 'hidden'
  };

  return (
    <div style={wrapperStyle}>
      <ReactQuill
        ref={quillRef}
        theme="snow"
        value={value || ''}
        onChange={onChange}
        placeholder={placeholder}
        modules={modules}
        onFocus={() => { onFocus && onFocus(); }}
        onChangeSelection={(range) => {
          if (range) {
            lastRangeRef.current = range;
          }
        }}
      />
      <style>{`
        .ql-toolbar.ql-snow {
          border: none;
          border-bottom: 1px solid var(--border);
          background: var(--surface-light, #f4f5f7);
        }
        .ql-container.ql-snow {
          border: none;
          min-height: 220px;
          font-size: 14px;
          color: var(--text-primary);
          background: var(--surface);
        }
      `}</style>
    </div>
  );
});

export default RichTextEditor;
